import React, { useEffect, useState } from 'react';
import { IUserNotificationDetail } from '@Shared.Angular/@types/core/contracts/queryModel/notifications/userNotificationDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { NotificationType } from '@Shared.Angular/@types/core/contracts/notifications/notificationType';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { UserNotificationItem } from './UserNotificationItem';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import {
  IUserNotificationDismissedMessage,
  IUserNotificationsReadMessage
} from '@Shared.Angular/@types/pubSub';

type Props = {
  onClose?: () => void;
  onUnreadDismissed?: () => void;
};

const pageSize = 10;

export const UserNotificationsPanel = ({ onClose }: Props) => {
  const [notificationList, setNotificationList] = useState<
    IUserNotificationDetail[]
  >([]);
  const [hasMoreUnreadNotifications, setHasMoreUnreadNotifications] =
    useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const userNotificationsApiService = getService('userNotificationsApiService');

  useEffect(() => {
    userNotificationsApiService.getNotifications(0).then((response) => {
      setNotificationList(response);
      setHasMoreUnreadNotifications(true);
    });
    const subscriberId = 'runner.user.notifications.panel';
    getService('pubsubService').subscribeLate(
      'SIGNALR_USER_NOTIFICATIONS_READ',
      (event, message: IUserNotificationsReadMessage) => {
        setNotificationList((currentList) =>
          currentList.filter(
            (notification) =>
              !message.userNotificationIds.includes(notification.id)
          )
        );
      },
      subscriberId
    );
    getService('pubsubService').subscribeLate(
      'SIGNALR_USER_NOTIFICATION_DISMISSED',
      (event, message: IUserNotificationDismissedMessage) => {
        setNotificationList((currentList) =>
          currentList.filter(
            (notification) => notification.id !== message.userNotificationId
          )
        );
      },
      subscriberId
    );
    return () => {
      getService('pubsubService').unsubscribeAll(subscriberId);
    };
  }, []);

  const getNotificationsByPageIndex = (requestedPageNumber: number): void => {
    userNotificationsApiService
      .getNotifications(requestedPageNumber)
      .then((response) => {
        setNotificationList((currentList) => currentList.concat(response));
        setHasMoreUnreadNotifications(
          response.length >= pageSize || requestedPageNumber === 0
        );
      });
  };

  const dismissNotification = (notification: IUserNotificationDetail): void => {
    userNotificationsApiService
      .dismissNotification(notification.id, false)
      .then(() => {
        setNotificationList((currentList) =>
          currentList.filter((item) => item.id !== notification.id)
        );
      });
  };

  const readNotification = (
    flowId?: Guid,
    notificationType?: NotificationType,
    stepTask?: Guid,
    isForPublishRequest?: boolean
  ): void => {
    onClose?.();
    if (notificationType === NotificationType.FlowComment) {
      getService('$state').go(STATE_MAPPINGS.flow, {
        flowId,
        showComments: true
      });
    } else if (notificationType === NotificationType.StepTaskComment) {
      getService('$state').go(STATE_MAPPINGS.flow, { flowId, stepTask });
    } else if (notificationType === NotificationType.FlowModelPublished) {
      getService('tempModelerUrlService').openModeler({ flowModelId: flowId });
    } else if (notificationType === NotificationType.FlowModelFeedback) {
      getService('tempModelerUrlService').openModeler({
        flowModelId: flowId,
        openFeedback: true,
        loadPublishRequestSchema: isForPublishRequest
      });
    }

    if (flowId) userNotificationsApiService.readNotification(flowId);
  };

  const getMoreNotifications = (): void => {
    const nextPageNumber = pageNumber + 1;
    setPageNumber(nextPageNumber);
    getNotificationsByPageIndex(nextPageNumber);
  };

  const unreadNotifications = notificationList
    .filter((notification) => !notification.read)
    .sort(
      (left, right) =>
        new Date(String(right.commentDate)).getTime() -
        new Date(String(left.commentDate)).getTime()
    );
  const readNotifications = notificationList
    .filter((notification) => notification.read)
    .sort(
      (left, right) =>
        new Date(String(right.commentDate)).getTime() -
        new Date(String(left.commentDate)).getTime()
    );

  return (
    <div className="user-notifications-panel">
      {unreadNotifications.map((notification) => (
        <div key={notification.id}>
          <UserNotificationItem
            notification={notification}
            onDismiss={dismissNotification}
            onRead={(item) =>
              readNotification(
                item.flowId,
                item.notificationType,
                item.stepTaskId,
                item.isForPublishRequest
              )
            }
          />
        </div>
      ))}
      {readNotifications.map((notification) => (
        <div key={notification.id}>
          <UserNotificationItem
            notification={notification}
            onDismiss={dismissNotification}
            onRead={(item) =>
              readNotification(
                item.flowId,
                item.notificationType,
                item.stepTaskId,
                item.isForPublishRequest
              )
            }
          />
        </div>
      ))}
      <div className="user-notifications-panel__footer">
        {hasMoreUnreadNotifications ? (
          <TextButton
            type="secondary"
            onClick={getMoreNotifications}
            text={pageNumber >= 1 ? 'More' : 'View all'}
            testId="user-notifications-more"
          />
        ) : (
          <div data-testid="user-notifications-no-more">
            There are no more notifications
          </div>
        )}
      </div>
    </div>
  );
};
